/**
 * The plan's SEVENTH source: the **ground under the route** — `POST
 * /api/terrain/profile` and `GET /api/terrain/elev`, read into the form's
 * SafeAlt column and, as a fallback only, into the V7 Höhe cells.
 *
 * Same shape as `route.ts`, `aircraft.ts`, `wx.ts`, `geo.ts` and `winds.ts`:
 * everything above the `--- the network step ---` divider is a pure function over
 * the endpoint's answer and the plan's own values, so the rules are pinned down by
 * tests instead of by clicking, and the store owns *when* it runs and which cells
 * it may touch (`PlanStore.setTerrain`, source `'terrain'`).
 *
 * What it fills, and nothing else:
 *   · `leg<N>_safealt` — the suggested Sicherheitshöhe, written unattended
 *   · `aip_p1_elev` / `aip_p2f_elev` — the aerodrome elevation, and ONLY where
 *     openAIP publishes none (`geo.ts` owns that cell and takes it back if it
 *     turns out to have an answer; see `PlanStore.setGeo`)
 *
 * Five decisions worth writing down.
 *
 * **The profile is read unattended, and the SafeAlt column is filled from it.**
 * The numbers appear beside the route as soon as it is drawn (debounced, exactly
 * like the airspace check), and the suggestion goes into `leg<N>_safealt` in the
 * same pass — a derived value like the forecast wind, marked as one, overruled by
 * typing over it and handed back by clearing it. The LBA form's Sicherheitshöhe
 * stays the pilot's number: what changes is that they now correct a proposal
 * instead of facing an empty column. Every later profile restates the cells it
 * owns, so a value can never keep the mark „aus dem Geländeprofil (Korridor
 * 5 NM)" while the profile it names says something else ({@link safeAltValues}).
 *
 * **SafeAlt is written in thousands of feet with one decimal** — `"2.5"`, `"9.0"`.
 * That is what the printed form's own example writes ({@link safeAltCell}), and it
 * is what `fields.ts` types the cell as (`decimal`). 9 500 ft is `"9.5"`, never
 * `"9500"`.
 *
 * **The elevation fallback is a point, not the corridor maximum.** `samples[0]` of
 * the first leg is the highest ground within the corridor at that point — 4 274 ft
 * beside LJLJ, whose field elevation is 1 214 ft — so the V7 cell is filled from
 * `GET /api/terrain/elev` at the waypoint itself, and only when that waypoint IS
 * the aerodrome the row is about (its ICAO code equals `von` / `nach`).
 *
 * **`msa_ft` is a suggestion, and the assumptions say so.** The endpoint's
 * `assumptions` codes are rendered wherever the numbers are, the same contract as
 * the airspace check's: `great_circle_leg`, `dem_z10`,
 * `obstacle_top_is_lower_bound` (openAIP knows a height for ~15 % of obstacles —
 * for the rest the figure is the GROUND and the top is higher, which is why the
 * UI prints "≥"), `msa_is_a_suggestion`.
 *
 * **A leg the archive does not cover has no number at all.** `msa_ft: null` means
 * the DEM had no tile anywhere along the leg and no obstacle was found; the row
 * says so rather than showing a 0 that looks like sea level. A `0 ft` reading, on
 * the other hand, is a real measurement (the Adriatic ships as tiles of 0 m) and
 * is shown as one.
 */

import * as api from './api';
import type { TerrainLeg, TerrainObstacle, TerrainProfile, TerrainSample } from './api';
import { LEG_NUMBERS, legField } from './fields';
import { MAX_ROUTE_LEGS, waypointIcao, type Waypoint } from './route';
import { DEFAULT_CORRIDOR_NM, type PlanStore, type TerrainLegSummary, type TerrainStatus } from './store';

export { DEFAULT_CORRIDOR_NM };

/** The endpoint's own attribution string; the fallback when a stored plan has none. */
export const TERRAIN_SOURCE =
  'Mapterhorn (terrain: various open data; see mapterhorn.com/attribution) · © openAIP contributors (CC BY-NC 4.0)';

/** How wide a corridor the pilot may read the profile over (`corridor_nm` is 1–10). */
export const CORRIDOR_OPTIONS: readonly number[] = [2, 5, 10];

/** One sample per NM: ~70 points on a long leg, which is what the sparkline wants. */
export const DEFAULT_STEP_NM = 1;

/** `plans.route` holds at most this many, and so does the endpoint. */
export const MAX_TERRAIN_WAYPOINTS = 60;

/* ================================================================== *
 * the cells
 * ================================================================== */

/**
 * The SafeAlt cell for a suggested MSA: **thousands of feet, one decimal**.
 *
 * The unit is not ours to choose — the LBA form's own worked example writes
 * `2.5`, `2.9` and `2.0` into that column (`packages/core/test/
 * fixtures-example-flight.json`), and `fields.ts` types it `decimal`. The
 * trailing `.0` is kept for the same reason: it is what the example prints, and
 * a bare `9` in a column of `2.5`s reads as 9 hundred feet at a glance.
 */
export function safeAltCell(msaFt: number | null | undefined): string {
  if (msaFt == null || !Number.isFinite(msaFt)) return '';
  return (Math.round(msaFt) / 1000).toFixed(1);
}

/** Every field id the `'terrain'` source may own in the route block. */
export const SAFEALT_IDS: readonly string[] = LEG_NUMBERS.map(j => legField(j, 'safealt'));

/** The two V7 cells the elevation fallback may own. Slot → cell, and the row it is about. */
export const ELEV_CELLS: readonly { slot: 'p1' | 'p2f'; id: string; icaoCell: string }[] = [
  { slot: 'p1', id: 'aip_p1_elev', icaoCell: 'von' },
  { slot: 'p2f', id: 'aip_p2f_elev', icaoCell: 'nach' },
];

/* ================================================================== *
 * one leg, as the panel shows it
 * ================================================================== */

/** The tallest obstacle of a leg — the one `max_obstacle_ft` is about. */
export function topObstacle(list: readonly TerrainObstacle[]): TerrainObstacle | null {
  let best: TerrainObstacle | null = null;
  for (const o of list) {
    if (o.elev_ft == null) continue;
    if (!best || o.elev_ft > (best.elev_ft ?? -Infinity)) best = o;
  }
  return best;
}

/**
 * What one leg looks like in the route panel. Everything is already resolved —
 * the component only formats it, so the rules are testable without a DOM.
 */
export interface TerrainRow {
  /** the FORM's leg number (1-based); `leg + 1` of the endpoint's own numbering */
  leg: number;
  from: string;
  to: string;
  distNm: number | null;
  maxTerrainFt: number | null;
  maxAtNm: number | null;
  /**
   * The tallest obstacle in the corridor, or null when the leg has none.
   *
   * `name` is what openAIP publishes and is often empty; `kind` is its type slug
   * (`tower`, `chimney`, `wind_turbine`, …) and is for the RENDERER to translate.
   * Substituting the slug into the sentence here is what printed „Hindernis
   * Obstacle 716 ft" into a German UI.
   */
  obstacle: { name: string; kind: string; ft: number | null; exact: boolean } | null;
  msaFt: number | null;
  /** `flat` | `mountain` | '' */
  msaRule: string;
  /** the value this profile writes into the leg's Sicherheitshöhe, or '' when it has none */
  safeAlt: string;
  /** the form has twelve leg rows; a thirteenth leg is flown but has no cell */
  writable: boolean;
  /** the drawing, or null when the leg has no usable sample */
  spark: Spark | null;
}

const legName = (waypoints: readonly Waypoint[], i: number): string => waypoints[i]?.name ?? '';

/** The panel's model for a fetched profile. */
export function terrainRows(
  legs: readonly TerrainLeg[],
  waypoints: readonly Waypoint[],
): TerrainRow[] {
  return legs.map(l => {
    const n = l.leg + 1;
    const top = topObstacle(l.obstacles);
    return {
      leg: n,
      from: legName(waypoints, l.leg),
      to: legName(waypoints, l.leg + 1),
      distNm: l.dist_nm,
      maxTerrainFt: l.max_terrain_ft,
      maxAtNm: l.max_terrain_at_nm,
      obstacle: top ? { name: top.name, kind: top.type, ft: top.elev_ft, exact: top.exact } : null,
      msaFt: l.msa_ft,
      msaRule: l.msa_rule,
      safeAlt: safeAltCell(l.msa_ft),
      writable: n >= 1 && n <= MAX_ROUTE_LEGS,
      spark: sparkline(l),
    };
  });
}

/**
 * The same model from what the plan REMEMBERS (`plans.meta.terrain`) rather than
 * from a fetch: the shared read-only view, which never asks the endpoint
 * anything. There is no profile behind it, so there are no samples, no obstacle
 * list and no drawing — the two numbers that were worth storing, and nothing
 * pretending to be more.
 */
export function storedRows(
  status: TerrainStatus | null | undefined,
  waypoints: readonly Waypoint[],
): TerrainRow[] {
  if (!status) return [];
  return status.legs.map(l => ({
    leg: l.leg,
    from: legName(waypoints, l.leg - 1),
    to: legName(waypoints, l.leg),
    distNm: null,
    maxTerrainFt: l.max_terrain_ft ?? null,
    maxAtNm: null,
    obstacle: null,
    msaFt: l.msa_ft ?? null,
    msaRule: l.msa_rule ?? '',
    safeAlt: safeAltCell(l.msa_ft ?? null),
    writable: l.leg >= 1 && l.leg <= MAX_ROUTE_LEGS,
    spark: null,
  }));
}

/* ================================================================== *
 * the sparkline
 * ================================================================== */

/** The drawing's own coordinate system — CSS stretches it to whatever width it gets. */
export const SPARK_W = 100;
export const SPARK_H = 24;
/** Half a stroke of air top and bottom, so the line is not clipped by the viewBox. */
const SPARK_PAD = 1;

/**
 * One leg's terrain profile as geometry: a few polylines, a tick per obstacle and
 * the MSA's own line. No chart library, and no component may compute any of it —
 * the arithmetic is here, where it can be tested.
 *
 * Two things it does deliberately:
 *
 * **A sample with no elevation BREAKS the line.** `elev_ft: null` means the DEM
 * has no tile there, which is not sea level and not zero; drawing through it
 * would invent a coastline. Each unbroken run becomes its own polyline.
 *
 * **The scale's top is the MSA**, not the terrain, so every leg is drawn against
 * the ceiling the pilot would fly at and the clearance is the visible gap. The
 * baseline is 0 ft AMSL, so two legs of the same route are comparable.
 */
export interface Spark {
  w: number;
  h: number;
  /** `"x,y x,y …"` per unbroken run of samples */
  lines: string[];
  /** the same runs closed to the baseline, for the fill */
  areas: string[];
  /** one per obstacle: a tick from its top down to the baseline */
  ticks: { x: number; y: number; exact: boolean }[];
  /** the suggested MSA's line, or null when the leg has none */
  msaY: number | null;
  /** what the top of the box is, in feet */
  topFt: number;
}

export function sparkline(
  leg: { samples: readonly TerrainSample[]; obstacles?: readonly TerrainObstacle[]; dist_nm: number; msa_ft?: number | null },
  size: { w?: number; h?: number } = {},
): Spark | null {
  const w = size.w ?? SPARK_W;
  const h = size.h ?? SPARK_H;
  const samples = leg.samples ?? [];
  const obstacles = leg.obstacles ?? [];
  if (!samples.some(s => s.elev_ft != null) && obstacles.length === 0) return null;

  let topFt = 0;
  for (const s of samples) if (s.elev_ft != null && s.elev_ft > topFt) topFt = s.elev_ft;
  for (const o of obstacles) if (o.elev_ft != null && o.elev_ft > topFt) topFt = o.elev_ft;
  if (leg.msa_ft != null && leg.msa_ft > topFt) topFt = leg.msa_ft;
  // a leg entirely at sea level still needs a box to draw in
  if (topFt <= 0) topFt = 1000;

  const span = Math.max(1e-6, leg.dist_nm || samples[samples.length - 1]?.nm || 1);
  const x = (nm: number): number => round2(Math.max(0, Math.min(1, nm / span)) * w);
  const y = (ft: number): number => round2(SPARK_PAD + (1 - Math.max(0, ft) / topFt) * (h - 2 * SPARK_PAD));

  const lines: string[] = [];
  const areas: string[] = [];
  let run: { x: number; y: number }[] = [];
  const flush = (): void => {
    if (run.length === 0) return;
    lines.push(run.map(p => p.x + ',' + p.y).join(' '));
    // a single point has no line; it still gets a (degenerate) area so it is visible
    const first = run[0];
    const last = run[run.length - 1];
    if (first && last) {
      areas.push(
        'M' + first.x + ',' + h + ' L' + run.map(p => p.x + ',' + p.y).join(' L') + ' L' + last.x + ',' + h + ' Z',
      );
    }
    run = [];
  };
  for (const s of samples) {
    if (s.elev_ft == null) {
      flush();
      continue;
    }
    run.push({ x: x(s.nm), y: y(s.elev_ft) });
  }
  flush();

  const ticks = obstacles
    .filter(o => o.elev_ft != null)
    .map(o => ({ x: x(o.dist_nm), y: y(o.elev_ft ?? 0), exact: o.exact }));

  return {
    w,
    h,
    lines,
    areas,
    ticks,
    msaY: leg.msa_ft == null ? null : y(leg.msa_ft),
    topFt,
  };
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/* ================================================================== *
 * what goes into the form
 * ================================================================== */

/**
 * The SafeAlt cells the profile on screen claims.
 *
 * With no `legs` it is the WHOLE domain, with `''` for every leg the current
 * profile has nothing to say about — that is what makes a leg the route no
 * longer has give its cell back, exactly as the winds do. With a `legs` list it
 * is those legs' cells and nothing else.
 *
 * What may actually be written is the store's decision (`setTerrain`): an empty
 * cell or one still terrain-derived is (re)written, and a Sicherheitshöhe the
 * pilot typed is the pilot's for good.
 */
export function safeAltValues(
  rows: readonly TerrainRow[],
  legs?: readonly number[],
): Map<string, string> {
  const byLeg = new Map<number, string>();
  for (const r of rows) if (r.writable) byLeg.set(r.leg, r.safeAlt);
  const out = new Map<string, string>();
  if (legs) {
    for (const n of legs) {
      const v = byLeg.get(n);
      if (v) out.set('leg' + n + '_safealt', v);
    }
    return out;
  }
  SAFEALT_IDS.forEach((id, i) => out.set(id, byLeg.get(LEG_NUMBERS[i] ?? 0) ?? ''));
  return out;
}

/** How many of the legs on screen have a SafeAlt to offer. */
export const writableRows = (rows: readonly TerrainRow[]): TerrainRow[] =>
  rows.filter(r => r.writable && r.safeAlt !== '');

/* ---------------- the elevation fallback ---------------- */

/** One V7 Höhe cell the DEM may answer for, and where to read it. */
export interface ElevNeed {
  slot: 'p1' | 'p2f';
  id: string;
  lat: number;
  lon: number;
}

/**
 * Which aerodrome elevations the terrain may fill in, right now.
 *
 * Three conditions, all of them necessary:
 *   · the cell is EMPTY or already this source's — openAIP's published figure and
 *     anything the pilot typed both outrank a DEM reading
 *   · the route really starts (or ends) at the aerodrome the row is about: the
 *     waypoint's own ICAO code has to equal `von` / `nach`, or the number would
 *     be the elevation of some other place entirely
 *   · there is a waypoint at all
 */
export function elevNeeds(store: PlanStore): ElevNeed[] {
  const wps = store.route?.waypoints ?? [];
  if (wps.length === 0) return [];
  const out: ElevNeed[] = [];
  for (const cell of ELEV_CELLS) {
    const w = cell.slot === 'p1' ? wps[0] : wps[wps.length - 1];
    if (!w) continue;
    // the destination of a one-waypoint route is its departure; the row is not about it
    if (cell.slot === 'p2f' && wps.length < 2) continue;
    const icao = waypointIcao(w);
    if (!icao || icao !== store.val(cell.icaoCell).trim().toUpperCase()) continue;
    const owned = store.derivedSource(cell.id) === 'terrain';
    if (store.val(cell.id) !== '' && !owned) continue;
    out.push({ slot: cell.slot, id: cell.id, lat: w.lat, lon: w.lon });
  }
  return out;
}

/** The V7 cells the DEM answered for. A point with no tile writes nothing. */
export function elevValues(
  needs: readonly ElevNeed[],
  points: readonly { elev_ft: number | null }[],
): Map<string, string> {
  const out = new Map<string, string>();
  needs.forEach((need, i) => {
    const ft = points[i]?.elev_ft;
    if (ft == null || !Number.isFinite(ft)) return;
    out.set(need.id, String(Math.round(ft)));
  });
  return out;
}

/* ---------------- what the plan remembers ---------------- */

/** The per-leg summary `plans.meta.terrain` keeps (see {@link TerrainStatus}). */
export function terrainSummary(profile: TerrainProfile): TerrainLegSummary[] {
  return profile.legs.map(l => ({
    leg: l.leg + 1,
    ...(l.max_terrain_ft != null ? { max_terrain_ft: l.max_terrain_ft } : {}),
    ...(l.msa_ft != null ? { msa_ft: l.msa_ft } : {}),
    ...(l.msa_rule ? { msa_rule: l.msa_rule } : {}),
  }));
}

export function terrainStatusOf(profile: TerrainProfile, now: Date): TerrainStatus {
  return {
    fetched_at: now.toISOString().replace(/\.\d{3}Z$/, 'Z'),
    corridor_nm: profile.corridor_nm,
    source: profile.source || TERRAIN_SOURCE,
    legs: terrainSummary(profile),
  };
}

/* ---------------- when to ask again ---------------- */

/**
 * What a profile depends on, as one comparable string: where the waypoints are
 * and how wide the corridor is. The altitudes are deliberately NOT in it — the
 * ground does not move when a Höhe/FL cell is typed into, and re-reading a
 * 5 000-sample profile for that would be a request nobody asked for.
 */
export function terrainSignature(waypoints: readonly Waypoint[], corridorNm: number): string {
  return (
    String(corridorNm) +
    '|' +
    waypoints.map(w => w.lat.toFixed(4) + ',' + w.lon.toFixed(4)).join(';')
  );
}

/* ================================================================== *
 * --- the network step ---
 * ================================================================== */

export interface TerrainLoad {
  profile: TerrainProfile;
  rows: TerrainRow[];
  status: TerrainStatus;
  /** the V7 Höhe cells this pass filled from the DEM (openAIP had none) */
  elev: string[];
  /** legs whose Sicherheitshöhe the pilot owns — the suggestion was not written there */
  kept: number[];
}

/**
 * Read the profile for the plan's route and remember it — the impure function
 * here, and the only one that knows about `PlanStore`.
 *
 * `null` when the plan has no leg to ask about.
 *
 * It WRITES THE SUGGESTION: the Sicherheitshöhe column is filled from the
 * profile the way the W/V column is filled from the forecast — a derived value,
 * marked as one, overruled by typing over it and handed back by clearing it
 * (`setTerrain` enforces all three). There is no button, because a suggestion
 * nobody pressed the button for is a suggestion that never reached the paper:
 * the column printed empty on every plan whose pilot did not know the button was
 * there. The same pass keeps a taken cell honest — restated from the profile
 * that has just arrived, cleared when the route loses that leg.
 *
 * It also writes, without asking, the aerodrome elevation openAIP does not
 * publish — a V7 cell that would otherwise stay empty, from the same DEM, and
 * taken back by `setGeo` the moment openAIP turns out to have an answer.
 */
export async function loadTerrain(
  store: PlanStore,
  opts: { corridorNm?: number; stepNm?: number; signal?: AbortSignal; now?: Date } = {},
): Promise<TerrainLoad | null> {
  const waypoints = (store.route?.waypoints ?? []).slice(0, MAX_TERRAIN_WAYPOINTS);
  if (waypoints.length < 2) return null;
  const corridorNm = opts.corridorNm ?? DEFAULT_CORRIDOR_NM;
  const before = terrainSignature(waypoints, corridorNm);

  const profile = await api.fetchTerrainProfile(waypoints, {
    corridorNm,
    stepNm: opts.stepNm ?? DEFAULT_STEP_NM,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  // the route may have moved on while the profile was out — a slow answer must not
  // describe yesterday's ground. The caller re-runs on every signature change.
  if (terrainSignature(store.route?.waypoints ?? [], corridorNm) !== before) return null;

  const status = terrainStatusOf(profile, opts.now ?? new Date());
  const rows = terrainRows(profile.legs, store.route?.waypoints ?? []);
  const needs = elevNeeds(store);
  let elev = new Map<string, string>();
  if (needs.length) {
    try {
      const answer = await api.fetchElevations(needs, opts.signal);
      elev = elevValues(needs, answer.points ?? []);
    } catch {
      /* the profile is the point of this call; a missing field elevation is not */
    }
  }
  // the whole SafeAlt domain, restated from the NEW profile: nothing is left
  // contradicting the numbers printed next to it, and a leg the route no longer
  // has gives its cell back
  const values = new Map(safeAltValues(rows));
  for (const [id, v] of elev) values.set(id, v);
  // a cell the pilot typed into is not ours to write; the panel says which legs
  // those are, so a plan whose SafeAlt column is half suggestion and half
  // decision reads as one
  const kept: number[] = [];
  for (const [id, v] of values) {
    if (!v || !SAFEALT_IDS.includes(id)) continue;
    if (store.val(id) !== '' && store.derivedSource(id) !== 'terrain') {
      kept.push(Number(/^leg(\d+)_safealt$/.exec(id)?.[1] ?? 0));
    }
  }
  store.setTerrain(values, status);

  return {
    profile,
    rows,
    status,
    elev: [...elev.keys()],
    kept,
  };
}

export type { TerrainLeg, TerrainProfile, TerrainStatus };
